import { AbortError } from "./abortError";
import { RetryError } from "./retryError";
import { TimeoutError } from "./timeoutError";
import * as self from "./wait";

/**
 * Optional settings that control the behavior of a wait operation.
 */
export interface WaitForOptions {
  /**
   * An `AbortSignal` that allows the wait to be cancelled early.
   *
   * If the signal is already aborted or is aborted before the wait completes,
   * the returned promise will reject with an `AbortError`.
   */
  signal?: AbortSignal;

  /**
   *  A callback invoked before each retry.
   *
   * Receives the time to wait before the operation will be retried, 
   * in milliseconds. If the callback returns `false`, the wait will be 
   * aborted and a `RetryError` will be thrown.
   *
   * @param ms - The time to wait for the next retry attempt, in milliseconds.
   *
   * @returns `false` to cancel the retry; any other value continues retrying.
   */
  onRetry?: (ms: number) => unknown;

  /**
   * Maximum duration to wait before timing out, in milliseconds.
   *
   * If the wait has not completed within this duration, the returned promise
   * will reject with a `TimeoutError`.
   */
  timeout?: number;
}

/**
 * Waits for the specified number of milliseconds.
 *
 * Supports cancellation via `AbortSignal`.
 *
 * @param ms - The number of milliseconds to wait.
 * @param signal - Optional abort signal to cancel the wait early.
 *
 * @returns A promise that resolves after the delay, or rejects with
 *          `AbortError` if cancelled.
 */
export function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortError());
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onReject);
    };

    const onReject = () => {
      cleanup();
      reject(new AbortError());
    };

    const onResolve = () => {
      cleanup();
      resolve();
    };

    signal?.addEventListener("abort", onReject, { once: true });
    timeoutId = setTimeout(onResolve, ms);
  });
}

/**
 * Waits until the specified high-resolution timestamp is reached.
 *
 * This function uses repeated calls to `wait()` and `performance.now()`
 * to align execution with the given target time.
 *
 * @param ts - Target timestamp (in milliseconds) relative to `performance.now()`.
 * @param signal - Optional abort signal to cancel the wait.
 *
 * @returns A promise that resolves when the current time is at or beyond `ts`,
 *          or rejects with `AbortError` if cancelled.
 */
export async function waitUntil(
  ts: number,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) {
    throw new AbortError();
  }

  let now = performance.now();
  while (now < ts) {
    await self.wait(ts - now, signal);
    now = performance.now();
  }
}

/**
 * Repeatedly evaluates a callback until it returns a non-positive number,
 * indicating that no further wait is needed.
 *
 * This function is useful for polling-based logic where the caller provides
 * a dynamic wait duration (in milliseconds) before retrying.
 *
 * Supports cancellation, retry control, and timeouts.
 *
 * @param callbackfn A function that returns the time to wait before retrying, in milliseconds.
 *                     A value <= 0 indicates the wait is complete.
 *
 * @param options Optional controls for timeout, aborting, and retry logic.
 *
 * @returns A promise that resolves when the condition is met,
 *          or rejects if aborted, timed out, or the retry is cancelled.
 *
 * @throws {AbortError} if the provided signal is aborted before resolution.
 * @throws {RetryError} if the retry is cancelled by the `onRetry` callback returning `false`.
 * @throws {TimeoutError} if the wait exceeds the specified timeout.
 */
export async function waitFor(
  callbackfn: () => number,
  { signal, onRetry, timeout }: WaitForOptions = {},
): Promise<void> {
  if (signal?.aborted) {
    throw new AbortError();
  }

  // Optional timeout setup
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let timeoutSignal: AbortSignal | undefined;
  if (timeout != null) {
    const controller = new AbortController();
    timeoutSignal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeout);
  }

  // Combine signals if needed
  const signals = [signal, timeoutSignal].filter(signal => signal != null);
  const combinedSignal = (signals.length > 0) ? AbortSignal.any(signals) : undefined;

  try {
    while (true) {
      const ms = callbackfn();
      if (ms <= 0) {
        return;
      }
      if (onRetry?.(ms) === false) {
        throw new RetryError();
      }
      await wait(ms, combinedSignal);
    }
  } catch (error) {
    throw (timeoutSignal?.aborted) ? new TimeoutError() :  error;
  } finally {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
  }
}
